import { isBoolean } from 'lodash';
import { unstable_batchedUpdates } from 'react-dom';

import { LoginSuccessResponse, LoginRequest, sendErrorToServer } from '..';
import { NEW_EXAMS_AVAILABLE_MESSAGE } from '../../../cnusr/components/modals/newExamDialog';
import { resetActivityMonitor } from '../../legacy/activityMonitor';
import { initializePingPolice } from '../../legacy/pingPolice';
import { bannerState, problemState } from '../../stores';
import { setLastMsgId } from '../../stores/globalState';
import { setPracticeLockFromLogin } from '../../stores/practiceLockStore';
import {
  WELCOME,
  studentAppModalState,
} from '../../stores/studentAppModalStore';
import { timeStuckStore } from '../../stores/timeStuckStore';
import { userState } from '../../stores/userStore';
import { newWork, workState } from '../../stores/workStore';
import {
  DEFAULT_ORDER_BY,
  ID,
  NO_WORK,
  hasExamWorkType,
  ProblemObject,
  ThemeOption,
} from '../../types';
import { gmmAlert } from '../../utils';
import { harvestTimeSeen, isMobile, logHistory } from '../../utils/gmmUtils';
import { alerts } from '../alerts';
import { configureExtremeLogger } from '../extremeLogger';
import {
  normalizeExamData,
  notifyNewExam,
  processAvailableWork,
  updateAllTime,
  updateGameCredits,
  updatePermitReadAloud,
  updateReplacementsPerDay,
  updateRequireStudentsToFinishAssignedSkills,
  updateSwitchToSpiralReviewSettings,
} from '../responseHandlerShared';

interface LoginData {
  packet: LoginRequest;
  data: LoginSuccessResponse;
}

export const processLogin = async ({
  packet,
  data,
}: LoginData): Promise<void> => {
  unstable_batchedUpdates(() => {
    try {
      studentAppModalState().setLoading(true, 'loginReact');

      const internal = packet.loginInternal;

      logHistory(internal ? 'loginInternal' : 'login');

      resetActivityMonitor();

      timeStuckStore.getState().setTimeStuck(data.timeStuck);

      // initialize timeSeenStart
      harvestTimeSeen();

      const practiceLockedForNonExamWork = isPracticeLockedForNonExamWork(data);

      // Temporary patch for old WF strategy of sending a selectedRestoreId
      // and a one-item dollarRestoreIds even if there are no problems.
      if (!practiceLockedForNonExamWork && !data.problems) {
        data.selectedRestoreId = undefined;
        data.dollarRestoreIds = [];
        data.problems = {} as Record<ID, ProblemObject>;
        problemState().setNoProblemsOnLogin(true);
      }

      userState().updateUserValues({
        isGhost: !!data.isGhost,
        studentName: data.rN,
        orgName: data.orgName!,
        className: data.clasName!,
        teacherName: data.teacherName!,
        sicId: data.sicId!,
        redisSessionId: data.ss,
        neverCrown: data.neverCrown!,
        userId: data.userId,
      });

      if (data.guid) userState().updateUserValues({ guid: data.guid });

      configureExtremeLogger(data.extremeLogger);

      bannerState().setAllowHand(data.aH);
      updatePermitReadAloud(data.permitReadAloud);
      bannerState().setTeacherOnline(data.tO === true);
      bannerState().setBlockGames(!!data.bg);
      bannerState().setBlockGamesClass(!!data.bgclas);
      updateReplacementsPerDay(data.replacementsPerDay);
      bannerState().setReplacementsUsedToday(data.replacementsUsedToday);
      bannerState().setAllowReplacementsWhenTeacherOnline(
        data.allowReplacementsWhenTeacherOnline
      );

      if (data.allTimeEligibleOnly) {
        updateAllTime(data.allTimeEligibleOnly);
      }

      setLastMsgId(data.lastMsgId);

      bannerState().setClassHighestToday(data.clasHighestToday);
      bannerState().setPointsThisWeek(data.sw);
      bannerState().setPointsToday(data.s);

      if (data.hw || data.hw === 0) {
        bannerState().setDailyGoal(data.hw);
      }

      updateGameCredits(
        data.gameCredits,
        data.pointsTowardGameCredit,
        data.pointsForGameCredit
      );

      bannerState().setTeacherName(data.teacherName);
      bannerState().setClassName(data.clasName);
      if (data.oC) bannerState().setOtherClasses(data.oC);

      bannerState().setThemeOption(data.theme as ThemeOption);
      problemState().setOrderBy(data.orderBy || DEFAULT_ORDER_BY, false);
      bannerState().setEffects(!!data.effects);
      bannerState().setShowTopScores(!!data.showTopScores);
      bannerState().setShowClock(data.showClock);
      bannerState().setUseNativeKeyboard(!!data.nativeKeyboard);
      bannerState().setTouchKeyboard(!!data.forceTouchKeyboard);
      bannerState().setAllowExamsOnMobile(data.allowExamsOnMobile);

      if (data.games) studentAppModalState().setAvailableGames(data.games);

      if (data.examMessages) {
        studentAppModalState().setAvailableExams(
          normalizeExamData(data.examMessages)
        );
      }

      bannerState().setLoginCount(data.loginCount);

      if (practiceLockedForNonExamWork) {
        workState().setCurrentWork(NO_WORK);
        studentAppModalState().setAvailableWork([]);
        studentAppModalState().setAvailableGames([]);
      } else {
        updateSwitchToSpiralReviewSettings(data.switchToSpiralReviewSettings!);
        updateRequireStudentsToFinishAssignedSkills(
          data.requireStudentsToFinishAssignedSkills
        );

        if (isBoolean(data.allowWorkOnAnyProblems)) {
          problemState().setDollarsOnly(!data.allowWorkOnAnyProblems);
        }

        newWork(data);
        processAvailableWork(data.availableWork);
      }

      setPracticeLockFromLogin(data.manualPracticeLocked);

      initializePingPolice();

      if (!internal) {
        if (
          !maybeNotifyNewExams(data) &&
          !practiceLockedForNonExamWork &&
          bannerState().loginCount < 5
        ) {
          studentAppModalState().setCurrentModal(WELCOME);
        }
      }
    } catch (err: unknown) {
      gmmAlert(alerts.loginFailed);

      const errorObject =
        err instanceof Error
          ? { message: err.message, name: err.name }
          : { message: 'no error message', name: 'no error name' };

      sendErrorToServer(
        `login logic failed, err: ${JSON.stringify(errorObject, null, 2)}`,
        true
      );
    } finally {
      studentAppModalState().setLoading(false, 'loginReact');
    }
  });
};

function isPracticeLockedForNonExamWork(
  loginData: LoginSuccessResponse
): boolean {
  return !!loginData.manualPracticeLocked && !hasExamWorkType(loginData);
}

function maybeNotifyNewExams(loginData: LoginSuccessResponse): boolean {
  if (isMobile() && !bannerState().allowExamsOnMobile) return false;

  const availableExams = loginData.availableExams;

  if (!availableExams?.length) return false;

  if (availableExams.length === 1) {
    notifyNewExam(availableExams[0].sitId);

    return true;
  }

  studentAppModalState().setNewExamDialog({
    message: NEW_EXAMS_AVAILABLE_MESSAGE,
  });

  return true;
}
